
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import Papa from 'papaparse';
import { validateRecord, validateForDuplicates } from './validation';
import { CsvRecord } from './types';
import { CsvColumnRequirements } from './CsvColumnRequirements';
import { DragDropUpload } from './DragDropUpload';
import { DuplicateValidation } from './DuplicateValidation';
import { toast } from 'sonner';
import { ArrowRight, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface CsvUploadStepProps {
  onUploadComplete: (uploadedRecords: CsvRecord[]) => void;
}

export const CsvUploadStep = ({ onUploadComplete }: CsvUploadStepProps) => {
  const [file, setFile] = useState<File | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [duplicateEmails, setDuplicateEmails] = useState<string[]>([]);
  const [internalDuplicates, setInternalDuplicates] = useState<string[]>([]);

  const checkForExistingEmails = async (emails: string[]): Promise<string[]> => {
    if (emails.length === 0) return [];
    
    const { data: existingImports } = await supabase
      .from('user_imports')
      .select('email')
      .in('email', emails);
    
    return existingImports?.map(record => record.email) || [];
  };

  const handleParseCsv = async () => {
    if (!file) return;

    setIsProcessing(true);
    toast.info('Processing CSV file...');
    
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: async (results) => {
        if (results.errors.length > 0) {
          console.error('CSV parsing errors:', results.errors);
          toast.error('Error parsing CSV file. Please check the format.');
          setIsProcessing(false);
          return;
        }

        // Validate for duplicates
        const { internalDuplicates: intDuplicates } = await validateForDuplicates(results.data);
        
        // Get unique emails from the CSV
        const uniqueEmails = [...new Set(results.data.map((row: CsvRecord) => row.email?.toLowerCase().trim()).filter(Boolean))];
        
        // Check for existing emails in database
        const existingEmails = await checkForExistingEmails(uniqueEmails);
        
        setDuplicateEmails(existingEmails);
        setInternalDuplicates(intDuplicates);
        
        // Process all records including duplicates
        const seenEmails = new Set<string>();
        const records: CsvRecord[] = results.data
          .filter((row: CsvRecord) => row.email) // Only filter out rows without email
          .map((row: CsvRecord, index: number) => {
            const email = row.email.toLowerCase().trim();
            const validation = validateRecord(row);
            
            // Check for duplicates
            let isDuplicate = false;
            let duplicateReason = '';
            
            if (existingEmails.includes(email)) {
              isDuplicate = true;
              duplicateReason = 'Already exists in database';
            } else if (seenEmails.has(email)) {
              isDuplicate = true;
              duplicateReason = 'Duplicate within CSV file';
            }
            
            seenEmails.add(email);
            
            return {
              id: `temp-${index}`,
              email: email, // Use the normalized lowercase email
              first_name: row.first_name || '',
              last_name: row.last_name || '',
              role: row.role || 'member',
              isValid: validation.isValid && !isDuplicate,
              validationErrors: validation.errors,
              selected: validation.isValid && !isDuplicate, // Auto-select valid, non-duplicate records
              isDuplicate,
              duplicateReason
            };
          });

        const validCount = records.filter(r => r.isValid && !r.isDuplicate).length;
        const invalidCount = records.filter(r => !r.isValid && !r.isDuplicate).length;
        const duplicateCount = records.filter(r => r.isDuplicate).length;
        
        if (validCount === 0) {
          toast.error('No valid records found in the CSV file');
        } else {
          let message = `Found ${validCount} valid records`;
          if (invalidCount > 0) message += `, ${invalidCount} invalid records`;
          if (duplicateCount > 0) message += `, ${duplicateCount} duplicates`;
          toast.success(message);
        }
        
        setIsProcessing(false);
        onUploadComplete(records);
      },
      error: (error) => {
        console.error('CSV parsing error:', error);
        setIsProcessing(false);
        toast.error('Error parsing CSV file: ' + error.message);
      }
    });
  };

  const hasIssues = duplicateEmails.length > 0 || internalDuplicates.length > 0;

  return (
    <div className="space-y-4">
      <CsvColumnRequirements />

      <div className="border-t pt-4">
        <DragDropUpload
          onFileSelect={setFile}
          selectedFile={file}
          isProcessing={isProcessing}
        />
      </div>

      {hasIssues && (
        <DuplicateValidation 
          duplicateEmails={duplicateEmails}
          internalDuplicates={internalDuplicates}
        />
      )}

      <div className="flex justify-end pt-3 border-t">
        <Button 
          onClick={handleParseCsv} 
          disabled={!file || isProcessing}
          className="flex items-center gap-2"
        >
          {isProcessing ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Processing...
            </>
          ) : (
            <>
              Parse CSV
              <ArrowRight className="h-4 w-4" />
            </>
          )}
        </Button>
      </div>
    </div>
  );
};
